import type { ReactNode } from "react";
import { useMemo, useState } from "react";

import { Carousel } from "../../../components/shared";
import { MinimizedProductTile } from "../../../components/shared/MinimizedProductTile";
import type { DisplayProductEntity } from "../../../view_models/products";

import { SectionHeading } from "./SectionHeading";

interface RelatedProductsCarouselProps {
  title: string;
  shopAllLink?: { href: string; text: string };
  products: DisplayProductEntity[];
  tilesPerView: number;
}

/**
 * A product rail interleaved between bands of search results. Borrowed from the
 * Reorder carousel prototype, but with a local header so the title matches every
 * other section on the page.
 */
export function RelatedProductsCarousel({
  title,
  shopAllLink,
  products,
  tilesPerView,
}: RelatedProductsCarouselProps) {
  const [navigationButtons, setNavigationButtons] = useState<ReactNode>(null);

  // Carousel's onNavigationButtonsReady effect lists `brands` as a dependency
  // and sets state from it, so an inline array literal here re-triggers the
  // effect on every render — an infinite update loop. Keep the identity stable.
  const carouselBrands = useMemo(
    () =>
      products.map((product) => ({
        imageUrl: product.imageUrl,
        brandName: product.brandName,
        brandAvatarUrl: product.brandAvatarUrl,
        // Same prototype assumption as the Reorder carousel: the viewing
        // retailer is an Insider, so always show "Free shipping".
        freeShipping: true,
        productName: product.name,
        price: product.priceDollars,
      })),
    [products]
  );

  return (
    <div className="border-fs-border-muted my-12 rounded-lg border px-6 py-4">
      <div className="flex items-baseline justify-between gap-2 pb-4">
        <SectionHeading size="small">{title}</SectionHeading>
        <div className="flex items-center gap-2">
          {shopAllLink ? (
            <a
              href={shopAllLink.href}
              className="type-fs-paragraph text-fs-text-primary underline"
            >
              {shopAllLink.text}
            </a>
          ) : null}
          {navigationButtons}
        </div>
      </div>
      <Carousel
        variant="brand-tile"
        tilesPerView={tilesPerView}
        onNavigationButtonsReady={setNavigationButtons}
        brands={carouselBrands}
        renderItem={(item) => (
          <MinimizedProductTile
            imageUrl={item.imageUrl}
            productName={item.productName ?? ""}
            price={item.price !== undefined ? `$${Math.round(item.price)}` : ""}
            brandName={item.brandName}
            freeShipping={item.freeShipping}
          />
        )}
      />
    </div>
  );
}
